import {type Maybe} from '@regle/core';
import {isFilled, fileType, file} from '@regle/rules';
import {useLogger} from '#imports';
import {getImageDimensions} from './dimensions.common';

export type ImageDimensionsPamameters = {width: number; height: number};
export const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/gif'] as const;

/**
 * Regle can return metadata from rules validators
 * @see {@link https://reglejs.dev/core-concepts/rules/reusable-rules#metadata}
 */
export type ImageDimensionsMetadata =
    | boolean
    | ({$valid: false; errorType: 'invalid-image-dimensions'} & ImageDimensionsPamameters)
    | {$valid: false; errorType: 'image-dimensions-unreadable'};

/**
 * Regle validator that checks the dimensions of an image.
 * @param options - The options for the validator.
 * @param options.value - The image to validate.
 * @param options.widthLimit - The maximum width of the image.
 * @param options.heightLimit - The maximum height of the image.
 * @param options.type - The type of validation to perform.
 * @returns The metadata for the Regle validation.
 */
export async function useImageDimensionsValidator(options: {
    value: Maybe<File>;
    widthLimit: number;
    heightLimit: number;
    type: 'max' | 'min';
}): Promise<ImageDimensionsMetadata> {
    const logger = useLogger();

    if (isFilled(options.value)) {
        if (file.exec(options.value) && fileType(ACCEPTED_FILE_TYPES).exec(options.value)) {
            try {
                const {width, height} = await getImageDimensions(options.value);
                let isValid = false;
                if (options.type === 'max') {
                    isValid = width <= options.widthLimit && height <= options.heightLimit;
                } else {
                    isValid = width >= options.widthLimit && height >= options.heightLimit;
                }
                if (isValid) {
                    return true;
                }
                return {
                    $valid: false,
                    errorType: 'invalid-image-dimensions',
                    width,
                    height,
                };
            } catch (error) {
                logger.error('Error getting image dimensions', {cause: error});

                return {
                    $valid: false,
                    errorType: 'image-dimensions-unreadable',
                };
            }
        }
        return true;
    }
    return true;
}
